import type { JSX, ReactNode } from 'react';
import RefreshIcon from '@assets/icons/learning-overview/quiz/refresh-cw.svg';
import BellIcon from '@assets/icons/notifications/bell.svg';
import CheckIcon from '@assets/icons/notifications/check.svg';
import ExclamationIcon from '@assets/icons/notifications/exclamation.svg';
import MinusIcon from '@assets/icons/notifications/minus.svg';
import WrongIcon from '@assets/icons/notifications/no-entry.svg';
import PlusIcon from '@assets/icons/notifications/plus.svg';
import InfoIcon from '@assets/icons/notifications/small-info.svg';
import UnlinkIcon from '@assets/icons/notifications/unlink.svg';
import { TEST_IDS } from '@constants/testIds.ts';
import { useLingui } from '@lingui/react/macro';
import { PATHWAY_TYPES } from '@routes/sidebar/admin-center/creator/___pages___/pathways/ui/form/constants';
import {
    SHARED_TAG_PILL_ABORTED,
    SHARED_TAG_PILL_ASSESSMENT,
    SHARED_TAG_PILL_ASSIGNED,
    SHARED_TAG_PILL_COMPLETED,
    SHARED_TAG_PILL_FAILED,
    SHARED_TAG_PILL_FINAL_REMINDER,
    SHARED_TAG_PILL_INFO,
    SHARED_TAG_PILL_LEARNING,
    SHARED_TAG_PILL_MAINTENANCE,
    SHARED_TAG_PILL_NEW,
    SHARED_TAG_PILL_OVERDUE,
    SHARED_TAG_PILL_PASSED,
    SHARED_TAG_PILL_REMINDER,
    SHARED_TAG_PILL_SECURITY,
    SHARED_TAG_PILL_SURVEY,
    SHARED_TAG_PILL_SYSTEM,
    SHARED_TAG_PILL_TASK_ASSESSOR_CHANGED,
    SHARED_TAG_PILL_TASK_CLOSED,
    SHARED_TAG_PILL_TASK_EVALUATION,
    SHARED_TAG_PILL_TASK_EXEMPTED,
    SHARED_TAG_PILL_TASK_EXEMPTION_REJECTED,
    SHARED_TAG_PILL_TASK_RECALLED_ASSESSOR_SIGN_OFF,
    SHARED_TAG_PILL_TASK_RECALLED_ASSESSOR_UNAVAILABLE,
    SHARED_TAG_PILL_TASK_RECALLED_BY_ASSESSEE,
    SHARED_TAG_PILL_TASK_RECALLED_CREW_SIGN_OFF,
    SHARED_TAG_PILL_TASK_RECALLED_ELEMENT_DELETED,
    SHARED_TAG_PILL_TASK_RECALLED_ELEMENT_UPDATED,
    SHARED_TAG_PILL_TASK_RECEIVED_FOR_EVALUATION,
    SHARED_TAG_PILL_TASK_RECEIVED_FOR_EXEMPTION,
    SHARED_TAG_PILL_TASK_REDO,
    SHARED_TAG_PILL_UNASSIGNED,
    SHARED_TAG_PILL_UNSUBSCRIBED,
} from '@routes/sidebar/notifications/ui/tag-pill/constants.ts';
import { Pill, PillIcon, PillText } from '@routes/sidebar/notifications/ui/tag-pill/styled.tsx';
import { translateItem } from '@utils/translations.ts';

const typeIconMap: Record<string, JSX.Element> = {
    Assigned: <PlusIcon />,
    Passed: <CheckIcon />,
    Completed: <CheckIcon />,
    Reminder: <BellIcon />,
    FinalReminder: <BellIcon />,
    Failed: <WrongIcon />,
    Aborted: <WrongIcon />,
    Overdue: <ExclamationIcon />,
    TaskRedo: <WrongIcon />,
    TaskAssessorChanged: <WrongIcon />,
    TaskExempted: <RefreshIcon />,
    TaskRecalledCrewSignOff: <ExclamationIcon />,
    TaskRecalledAssessorSignOff: <ExclamationIcon />,
    TaskRecalledElementUpdated: <ExclamationIcon />,
    TaskRecalledElementDeleted: <ExclamationIcon />,
    TaskRecalledAssessorUnavailable: <ExclamationIcon />,
    TaskReceivedForEvaluation: <CheckIcon />,
    TaskReceivedForExemption: <CheckIcon />,
    TaskRecalledByAssessee: <RefreshIcon />,
    TaskClosed: <CheckIcon />,
    LearningReassignedCategoryChanged: <RefreshIcon />,
    PathwayUnsubscribed: <UnlinkIcon />,
    PathwayUnasigned: <MinusIcon />,
};

const getPillIcon = (type: string): JSX.Element => {
    return typeIconMap[type] || <InfoIcon />;
};

export const PillTag = ({ type, source }: { type: string; source: string }) => {
    const SOURCE_TO_TEXT: Record<string, string> = {
        [PATHWAY_TYPES.LEARNING]: translateItem(SHARED_TAG_PILL_LEARNING),
        [PATHWAY_TYPES.ASSESSMENT]: translateItem(SHARED_TAG_PILL_ASSESSMENT),
        [PATHWAY_TYPES.SURVEY]: translateItem(SHARED_TAG_PILL_SURVEY),
        TaskEvaluation: translateItem(SHARED_TAG_PILL_TASK_EVALUATION),
        New: translateItem(SHARED_TAG_PILL_NEW),
        System: translateItem(SHARED_TAG_PILL_SYSTEM),
    };
    return (
        <Pill $hasIcon={false} $source={source} $type={type} data-cy={TEST_IDS.NOTIFICATIONS.TAG_PILL}>
            <PillText $source={source} $type={type} data-cy={TEST_IDS.NOTIFICATIONS.TAG_PILL_TEXT}>
                {SOURCE_TO_TEXT[source] ?? source}
            </PillText>
        </Pill>
    );
};

export const PillTagWithIcon = ({ type, source }: { type: string; source: string }) => {
    const { t } = useLingui();

    const TYPE_TO_TEXT: Record<string, ReactNode> = {
        Assigned: translateItem(SHARED_TAG_PILL_ASSIGNED),
        Passed: translateItem(SHARED_TAG_PILL_PASSED),
        Completed: translateItem(SHARED_TAG_PILL_COMPLETED),
        Reminder: translateItem(SHARED_TAG_PILL_REMINDER),
        FinalReminder: translateItem(SHARED_TAG_PILL_FINAL_REMINDER),
        Failed: translateItem(SHARED_TAG_PILL_FAILED),
        Aborted: translateItem(SHARED_TAG_PILL_ABORTED),
        Overdue: translateItem(SHARED_TAG_PILL_OVERDUE),
        Info: translateItem(SHARED_TAG_PILL_INFO),
        AdminCompletedSubmittedRequest: translateItem(SHARED_TAG_PILL_INFO),
        Security: translateItem(SHARED_TAG_PILL_SECURITY),
        PasswordChanged: translateItem(SHARED_TAG_PILL_SECURITY),
        Maintenance: translateItem(SHARED_TAG_PILL_MAINTENANCE),
        TaskRedo: translateItem(SHARED_TAG_PILL_TASK_REDO),
        TaskAssessorChanged: translateItem(SHARED_TAG_PILL_TASK_ASSESSOR_CHANGED),
        TaskRecalledCrewSignOff: translateItem(SHARED_TAG_PILL_TASK_RECALLED_CREW_SIGN_OFF),
        TaskExempted: translateItem(SHARED_TAG_PILL_TASK_EXEMPTED),
        TaskRecalledAssessorSignOff: translateItem(SHARED_TAG_PILL_TASK_RECALLED_ASSESSOR_SIGN_OFF),
        TaskRecalledElementUpdated: translateItem(SHARED_TAG_PILL_TASK_RECALLED_ELEMENT_UPDATED),
        TaskRecalledElementDeleted: translateItem(SHARED_TAG_PILL_TASK_RECALLED_ELEMENT_DELETED),
        TaskRecalledAssessorUnavailable: translateItem(SHARED_TAG_PILL_TASK_RECALLED_ASSESSOR_UNAVAILABLE),
        TaskExemptionRejected: translateItem(SHARED_TAG_PILL_TASK_EXEMPTION_REJECTED),
        TaskReceivedForEvaluation: translateItem(SHARED_TAG_PILL_TASK_RECEIVED_FOR_EVALUATION),
        TaskReceivedForExemption: translateItem(SHARED_TAG_PILL_TASK_RECEIVED_FOR_EXEMPTION),
        TaskRecalledByAssessee: translateItem(SHARED_TAG_PILL_TASK_RECALLED_BY_ASSESSEE),
        TaskClosed: translateItem(SHARED_TAG_PILL_TASK_CLOSED),
        LearningReassignedCategoryChanged: (
            <>
                <strong>{t`Reassigned`}</strong>: {t`Learning Category changed`}
            </>
        ),
        PathwayUnsubscribed: <> {translateItem(SHARED_TAG_PILL_UNSUBSCRIBED)}</>,
        PathwayUnasigned: <> {translateItem(SHARED_TAG_PILL_UNASSIGNED)}</>,
    };
    return (
        <Pill $hasIcon={true} $source={source} $type={type} data-cy={TEST_IDS.NOTIFICATIONS.TAG_PILL_WITH_ICON}>
            <PillIcon $source={source} $type={type} data-cy={TEST_IDS.NOTIFICATIONS.TAG_PILL_ICON}>
                {getPillIcon(type)}
            </PillIcon>
            <PillText $source={source} $type={type} data-cy={TEST_IDS.NOTIFICATIONS.TAG_PILL_TEXT}>
                {TYPE_TO_TEXT[type]}
            </PillText>
        </Pill>
    );
};
